import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { takeUntil } from "rxjs/operators";
import { ReplaySubject, combineLatest } from "rxjs";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { JobDetail } from "../../../models/jobDetail";
import { ModelType } from "../../../models/modelType";
import { CachedPaletteService } from "../../services/cached-palette.service";
import * as htmlToImage from "html-to-image";
import { NzTabsComponent } from "ng-zorro-antd/tabs";

@Component({
    selector: "app-model-visualizer",
    templateUrl: "./model-visualizer.component.html",
    styleUrls: ["./model-visualizer.component.less"],
    standalone: false
})
export class ModelVisualizerComponent implements OnInit, OnDestroy {
  @ViewChild('tabset', { static: false }) tabset: NzTabsComponent;

selectNextTab() {
const nextIndex = (this.tabset.nzSelectedIndex + 1) % this.tabset.tabs.length;
this.tabset.nzSelectedIndex = nextIndex;
}

selectPreviousTab() {
  const prevIndex =
    (this.tabset.nzSelectedIndex - 1 + this.tabset.tabs.length) %
    this.tabset.tabs.length;
  this.tabset.nzSelectedIndex = prevIndex;
}
  
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1); // unsubscribe to all subscriptions on destroy
  
  modelsForm!: UntypedFormGroup;

  maxSliceNumber: number = 1;
  activeModelGrid: {
    nx1: number;
    nx2: number;
    nx3: number;
    dx: number;
    is_2d: boolean;
  } = null;

  project_jobs: JobDetail[];
  comparisonResults: any = null;
  hasComparisonResults: boolean = false;
  isLoadingComparison: boolean = false;
  palettes: string[] = [];
  modelTypes1: ModelType[] = [];
  modelTypes2: ModelType[] = [];

  job1iteration: number = 0;
  job2iteration: number = 0;
  sliceNumber: number = 1;

  constructor(
    private fb: UntypedFormBuilder,
    private cachedProjectService: CachedProjectService,
    private nzNotificationService: NzNotificationService,
    private paletteService: CachedPaletteService
  ) {}

  ngOnInit(): void {
    this.paletteService.getPalettes().subscribe((data) => {
      this.palettes = data;
    });

    this.initializeForm();

    // Watch for job changes 
    combineLatest([
      this.modelsForm.get("model1.job").valueChanges,
      this.modelsForm.get("model2.job").valueChanges,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([job1, job2]) => {
        this.resetComparisonResults();
        this.handleJobChanges(job1, job2);
      });

    // Subscribe to dimension changes
    this.modelsForm.get("dimension").valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.updateSliceNumberMax();
        this.resetComparisonResults();
      });

    // Set up model type change handlers
    this.setupModelTypeHandlers();

    this.getJobs();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  

  getJobs() {
    this.cachedProjectService.currentProject
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (project) => {
          if (project) {
            this.cachedProjectService
              ._getProjectJobs(project.id, {
                fields: [
                  "id",
                  "job_name",
                  "iterations",
                  "project__id",
                  "model_grid__nx1",
                  "model_grid__nx2",
                  "model_grid__nx3",
                  "model_grid__dx",
                  "model_grid__is_2d",
                  "x2_is_inline",
                ],
              })
              .toPromise()
              .then((res) => {
                this.project_jobs = res["data"]
             
              })
              .catch((err) => {
                console.log(err);
                this.nzNotificationService.error(
                  "Error",
                  err.error?.error ||
                    "There was an unexpected error when fetching the project jobs"
                );
              });
          }
        },
        error: (err) => {
          console.log(err);
          this.nzNotificationService.error("Error", err);
        },
      });
  }

  

  submitForm(): void {
    if (this.modelsForm.valid) {
      // Reset the results flag when starting a new comparison
      this.hasComparisonResults = false;
      this.isLoadingComparison = true;
      
      
      const comparisonData = {
        model1: {
          job: this.modelsForm.get("model1.job").value["id"],
          iteration: this.job1iteration,
          type: this.modelsForm.get("model1.model_type").value,
          min_val: this.modelsForm.get("model1.min").value || null,
          max_val: this.modelsForm.get("model1.max").value || null,
          palette: this.modelsForm.get("model1.color_map").value
        },
        model2: {
          job: this.modelsForm.get("model2.job").value["id"],
          iteration: this.job2iteration,
          type: this.modelsForm.get("model2.model_type").value,
          min_val: this.modelsForm.get("model2.min").value || null,
          max_val: this.modelsForm.get("model2.max").value || null,
          palette: this.modelsForm.get("model2.color_map").value
        },
        difference: {
          palette: this.modelsForm.get("differencePalette").value,
          min_val: this.modelsForm.get("differenceRange").value ? -Math.abs(this.modelsForm.get("differenceRange").value) : null,
          max_val: this.modelsForm.get("differenceRange").value ? Math.abs(this.modelsForm.get("differenceRange").value) : null
        },
        interleaved: {
          extent: this.modelsForm.get("interleavedExtent").value
        },
        slice_dim: this.getDimensionValue(),
        slice_number: this.sliceNumber
      };

 

      
      this.cachedProjectService.compareModels(comparisonData)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response) => {
            this.isLoadingComparison = false;
            this.handleComparisonResponse(response);
          },
          error: (err) => {
            this.isLoadingComparison = false;
            console.error("Model comparison error:", err);
            this.nzNotificationService.error(
              "Error",
              err.error?.error || "Failed to compare models"
            );
          }
        });
    } else {
      this.markFormGroupTouched(this.modelsForm);
    }
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched({ onlySelf: true });
        control?.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  enableSharedSettings() {
    this.modelsForm.get("differenceRange").enable();
    this.modelsForm.get("dimension").enable();
    // Slice number is now managed by component variable, no form control to enable
  }

  disableSharedSettings() {
    this.modelsForm.get("differenceRange").disable();
    this.modelsForm.get("dimension").disable();
    // Reset slice number when disabling
    this.sliceNumber = 1;
    this.activeModelGrid = null;
  }

  setup2DSettings() {
    if (!this.activeModelGrid) return;
    
    if (this.activeModelGrid.is_2d) {
      // For 2D jobs, set dimension to "Crossline" and disable the selector
      // Since there's only one slice dimension available
      this.modelsForm.get("dimension").setValue("Crossline");
      this.modelsForm.get("dimension").disable();
    } else {
      // For 3D jobs, enable the dimension selector
      this.modelsForm.get("dimension").enable();
    }
  }

  updateSliceNumberMax() {
    if (!this.activeModelGrid) return;
    
    const dimension = this.modelsForm.get("dimension").value;
    const job1 = this.modelsForm.get("model1.job").value;
    
    switch (dimension) {
      case "Inline":
        this.maxSliceNumber = job1.x2_is_inline ? this.activeModelGrid.nx1 : this.activeModelGrid.nx2;
        break;
      case "Crossline":
        this.maxSliceNumber = job1.x2_is_inline ? this.activeModelGrid.nx2 : this.activeModelGrid.nx1;
        break;
      case "Depth":
        this.maxSliceNumber = this.activeModelGrid.nx3;
        break;
      default:
        this.maxSliceNumber = 1;
    }
    
    // Set initial slice number to middle of range
    const initialSlice = Math.ceil(this.maxSliceNumber / 2);
    this.sliceNumber = initialSlice;
  }

  is2DJob(): boolean {
    return this.activeModelGrid?.is_2d || false;
  }

  setDefaultPaletteForModelType(modelTypeId: string): string {
    // Set palette defaults based on model type - following same logic as model.component.ts
    if (
      modelTypeId.includes("Gradient") ||
      modelTypeId.includes("Update") ||
      modelTypeId.includes("Error")
    ) {
      return "red_white_blue";
    } else if (
      modelTypeId.includes("vdiff") ||
      modelTypeId.includes("RTM")
    ) {
      return "greyscale";
    } else if (
      modelTypeId == "Vp" ||
      modelTypeId == "PreMergeVp" ||
      modelTypeId == "Vs"
    ) {
      return "SEGRefl2";
    }
    
    // Default fallback
    return "Rainbow";
  }


  private getDimensionValue(): number {
    const dimension = this.modelsForm.get("dimension").value;
    const job1 = this.modelsForm.get("model1.job").value;


    switch (dimension) {
      case "Inline":
        return job1.x2_is_inline ? 1 : 2;
      case "Crossline":
        return job1.x2_is_inline ? 2 : 1;
      case "Depth":
        return 3;
    }
    
  }

  private handleComparisonResponse(response: any): void {
 

    this.comparisonResults = {
      ...response,
      // Pass job information for context
      job1: this.modelsForm.get("model1.job").value,
      job2: this.modelsForm.get("model2.job").value,
      // Pass the form values that were used for this comparison
      dimension: this.modelsForm.get("dimension").value,
      sliceNumber: this.sliceNumber,
      iteration1: this.job1iteration,
      iteration2: this.job2iteration,
      interleavedExtent: this.modelsForm.get("interleavedExtent").value
    };

    // Set flag to show that we now have results to display
    this.hasComparisonResults = true;

   
  }

  private fetchModelTypes(jobId: string, iteration: number, modelNumber: 1 | 2): void {
    
    this.cachedProjectService.getModelTypes(jobId, iteration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (modelTypes) => {
          if (modelNumber === 1) {
            this.modelTypes1 = modelTypes;
            this.validateModelTypeSelection(1);
          } else {
            this.modelTypes2 = modelTypes;
            this.validateModelTypeSelection(2);
          }
        },
        error: (err) => {
          console.error(`Failed to fetch model types for job ${modelNumber}:`, err);
          // Fall back to default model types
          const defaultModelTypes = [
            { id: "Vp", name: "Vp", processing: [] },
            { id: "RTM", name: "RTM", processing: [] },
            { id: "Density", name: "Density", processing: [] }
          ];
          
          if (modelNumber === 1) {
            this.modelTypes1 = defaultModelTypes;
            this.validateModelTypeSelection(1);
          } else {
            this.modelTypes2 = defaultModelTypes;
            this.validateModelTypeSelection(2);
          }
        }
      });
  }

  private validateModelTypeSelection(modelNumber: 1 | 2): void {
    const modelKey = `model${modelNumber}`;
    const modelTypes = modelNumber === 1 ? this.modelTypes1 : this.modelTypes2;
    const currentModelType = this.modelsForm.get(`${modelKey}.model_type`)?.value;

    // Check if current selection is valid
    const isValid = modelTypes.some(mt => mt.id === currentModelType);

    // Set to first available model type if current selection is invalid
    if (!isValid && modelTypes.length > 0) {
      const defaultType = modelTypes.find(mt => mt.id === 'Vp') || modelTypes[0];
      this.modelsForm.get(`${modelKey}.model_type`)?.setValue(defaultType.id);
      // Update the palette for the new model type
      const defaultPalette = this.setDefaultPaletteForModelType(defaultType.id);
      this.modelsForm.get(`${modelKey}.color_map`)?.setValue(defaultPalette);
    }
  }

  private resetComparisonResults(): void {
    this.hasComparisonResults = false;
    this.comparisonResults = null;
    this.isLoadingComparison = false;
  }

  getTabDisplayName(tab: string): string {
    switch (tab) {
      case 'job1':
        return 'Job 1 Model';
      case 'job2':
        return 'Job 2 Model';
      case 'difference':
        return 'Difference';
      case 'interleaved':
        return 'Interleaved View';
      default:
        return 'Chart';
    }
  }

  private initializeForm(): void {
    const defaultPalette = this.setDefaultPaletteForModelType("Vp");
    
    this.modelsForm = this.fb.group({
      model1: this.fb.group({
        job: [null, [Validators.required]],
        color_map: [defaultPalette, [Validators.required]],
        model_type: ["Vp", [Validators.required]],
        min: [null],
        max: [null],
      }),
      model2: this.fb.group({
        job: [null, [Validators.required]],
        color_map: [defaultPalette, [Validators.required]],
        model_type: ["Vp", [Validators.required]],
        min: [null],
        max: [null],
      }),
      // Common settings 
      dimension: [{ value: "Crossline", disabled: true }, Validators.required],
      differenceRange: [{ value: null, disabled: true }],
      interleavedExtent: [20, [Validators.required, Validators.min(0)]],
      differencePalette: ["red_white_blue", [Validators.required]],
    
    });
  }

  private handleJobChanges(job1: any, job2: any): void {
    if (!job1 || !job2) {
      this.disableSharedSettings();
      return;
    }

    if (this.areJobsCompatible(job1, job2)) {
      this.activeModelGrid = this.extractModelGrid(job1);
      this.enableSharedSettings();
      this.setup2DSettings();
      this.updateSliceNumberMax();
      this.fetchModelTypes(job1.id, this.job1iteration, 1);
      this.fetchModelTypes(job2.id, this.job2iteration, 2);
    } else {
      this.nzNotificationService.error(
        "Error",
        "The jobs you have selected have different model grids. These cannot be compared."
      );
      this.disableSharedSettings();
    }
  }

  private areJobsCompatible(job1: any, job2: any): boolean {
    return job1["model_grid__nx1"] === job2["model_grid__nx1"] &&
           job1["model_grid__nx2"] === job2["model_grid__nx2"] &&
           job1["model_grid__nx3"] === job2["model_grid__nx3"] &&
           job1["model_grid__dx"] === job2["model_grid__dx"] &&
           job1["model_grid__is_2d"] === job2["model_grid__is_2d"];
  }

  private extractModelGrid(job: any) {
    return {
      nx1: job["model_grid__nx1"],
      nx2: job["model_grid__nx2"],
      nx3: job["model_grid__nx3"],
      dx: job["model_grid__dx"],
      is_2d: job["model_grid__is_2d"],
    };
  }

  private setupModelTypeHandlers(): void {
    ['model1', 'model2'].forEach(modelKey => {
      this.modelsForm.get(`${modelKey}.model_type`).valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((modelType) => {
          if (modelType) {
            const defaultPalette = this.setDefaultPaletteForModelType(modelType);
            this.modelsForm.get(`${modelKey}.color_map`).setValue(defaultPalette);
          }
          this.resetComparisonResults();
        });
    });
  }

  // Slider change handlers
  onJob1IterationChange(value: number): void {
    this.job1iteration = value;
    this.resetComparisonResults();
    
    // Refetch model types when iteration changes for job1
    const job1 = this.modelsForm.get("model1.job").value;
    if (job1) {
      this.fetchModelTypes(job1.id, value, 1);
    }
  }

  onJob2IterationChange(value: number): void {
    this.job2iteration = value;
    this.resetComparisonResults();
    
    // Refetch model types when iteration changes for job2
    const job2 = this.modelsForm.get("model2.job").value;
    if (job2) {
      this.fetchModelTypes(job2.id, value, 2);
    }
  }

  onSliceNumberChange(value: number): void {
    this.sliceNumber = value;
    this.resetComparisonResults();
  }

  async takeScreenshot(): Promise<void> {
    if (!this.hasComparisonResults) {
      this.nzNotificationService.warning(
        'No Data',
        'Please generate a comparison first before taking a screenshot.'
      );
      return;
    }

    const activeIndex = this.tabset?.nzSelectedIndex ?? 0;
    const tabNames = ['job1', 'job2', 'difference', 'interleaved'];
    const currentTab = tabNames[activeIndex];

    const activeTabPane = document.querySelector('.ant-tabs-tabpane-active');
    if (!activeTabPane) {
      this.nzNotificationService.error(
        'Error',
        'Could not find active tab content for screenshot.'
      );
      return;
    }

    htmlToImage
      .toPng(activeTabPane as HTMLElement, {
        quality: 1.0,
        pixelRatio: window.devicePixelRatio,
      })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.href = dataUrl;
        
        const job1Name = this.modelsForm.get('model1.job').value?.job_name || 'job1';
        const job2Name = this.modelsForm.get('model2.job').value?.job_name || 'job2';
        const dimension = this.modelsForm.get('dimension').value;
        
        const fileName = `comparison-${currentTab}-${job1Name.substring(0, 10)}-${job2Name.substring(0, 10)}-${dimension}-iter${this.job1iteration}-iter${this.job2iteration}-slice${this.sliceNumber}.png`;
        
        link.download = fileName;
        link.click();
      })
      .catch((error) => {
        console.error('Error generating screenshot:', error);
        this.nzNotificationService.error(
          'Screenshot Failed',
          'Failed to generate screenshot. Please try again.'
        );
      });
  }

}


